import { MouseEvent, useEffect, useState } from "react";
import { Button } from "@kaltura/ds-react-components";
import { styled } from "@mui/material";
import { ConfirmationModal } from "@kaltura/mediaspace-shared-ui";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { ArrowLeft24Icon } from "@kaltura/ds-react-icons";

export interface AgentStudioHeaderProps {
    backUrl: string;
    hasChanges: boolean;
}

/**
 * Agent studio page header with the back button and confirmation.
 */
export const AgentStudioHeader = ({ backUrl, hasChanges }: AgentStudioHeaderProps) => {
    const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);

    const handleBack = (event: MouseEvent<HTMLButtonElement>) => {
        if (hasChanges) {
            setIsShowingConfirmation(true);
            event.preventDefault();
            return;
        }

        // Otherwise, continue to the default <a> click handler
    };

    const handleBackConfirmation = () => {
        // Cancel tab unload handler when confirming going back
        window.onbeforeunload = null;

        // The default <a> click handler will handle the navigation
    };

    // Prevent leaving the page when there are active changes
    useEffect(() => {
        window.onbeforeunload = hasChanges
            ? (event: BeforeUnloadEvent) => {
                  event.preventDefault();
                  event.returnValue = true;
              }
            : null;
    }, [hasChanges]);

    return (
        <>
            <StyledHeaderRow>
                <Button
                    variant={"borderless"}
                    color={"secondary"}
                    startIcon={<ArrowLeft24Icon />}
                    href={backUrl}
                    onClick={handleBack}
                >
                    {translate("Back")}
                </Button>
            </StyledHeaderRow>

            <ConfirmationModal
                open={isShowingConfirmation}
                title={translate("Unsaved changes")}
                text={translate("You have unsaved changes, are you sure you want to leave this page?")}
                yesButtonLabel={translate("Leave")}
                yesButtonColor={"danger"}
                noButtonLabel={translate("Keep editing")}
                yesHref={backUrl}
                yesCallback={handleBackConfirmation}
                noCallback={() => setIsShowingConfirmation(false)}
            />
        </>
    );
};

const StyledHeaderRow = styled("div")(({ theme }) => ({
    padding: theme.spacing(1, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));
